import React, { useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { ITables, BillItem, KITCHEN_ITEM_STATUS } from '../../../all-types'
import { updateCartItemOfTheTable } from '../../../services'
import { useAppDispatch } from '../../../redux-store/hooks'
import { KanbanColumn } from './KanbanColumn'
import { ItemCard } from './ItemCard'
import { CommonBtn } from '../../../components'
import { usePrinterSettings } from '../../../hooks'
import {
  Br,
  Cut,
  Line,
  Printer as RPrinter,
  Text,
  render,
} from 'react-thermal-printer'

type CheckedMap = Record<number, Record<number, boolean>>
const BOARD_HEIGHT = 'calc(100vh - 180px)'

const TABLE_COLORS = [
  '#FFF9ED',
  '#EAF4FF',
  '#F8E8FF',
  '#EAFBF1',
  '#FFF0F0',
  '#F0F5FF',
  '#F9F0FF',
  '#FDF6E4',
]
const colorForTable = (tableNo: number) =>
  TABLE_COLORS[Math.abs(tableNo) % TABLE_COLORS.length]

const getItemTime = (i: any) => {
  const t1 = i?.kitchInfo?.itemReceviedTime
    ? Number(i.kitchInfo.itemReceviedTime)
    : undefined
  const t2 = i?.createdAt ? Number(i.createdAt) : undefined
  return t1 ?? t2 ?? 0
}

type Props = {
  tables: ITables[]
  query: string
  tableFilter: string // 'all' or tableNo as string
}

export const DineInKanban: React.FC<Props> = ({
  tables,
  query,
  tableFilter,
}) => {
  const dispatch = useAppDispatch()
  const { printerUsb } = usePrinterSettings()
  const [checked, setChecked] = useState<CheckedMap>({})

  const allItems = useMemo(() => {
    const items: (BillItem & { tableNo: number })[] = []
    for (const t of tables) {
      const arr = t?.billSession?.items || []
      for (const it of arr) items.push({ ...it, tableNo: t.tableNo })
    }
    return items
  }, [tables])

  const textFiltered = useMemo(() => {
    if (!query) return allItems
    const q = query.toLowerCase()
    return allItems.filter(
      (i) =>
        i.itemName.toLowerCase().includes(q) || String(i.tableNo).includes(q)
    )
  }, [allItems, query])

  const statusFiltered = useMemo(
    () =>
      textFiltered.filter((i) =>
        [
          KITCHEN_ITEM_STATUS.IN_KITCHEN,
          KITCHEN_ITEM_STATUS.COOKING,
          KITCHEN_ITEM_STATUS.READY,
        ].includes(i.kitchInfo?.status as KITCHEN_ITEM_STATUS)
      ),
    [textFiltered]
  )

  const tableFilteredItems = useMemo(() => {
    if (tableFilter === 'all') return statusFiltered
    const tNo = Number(tableFilter)
    return statusFiltered.filter((i) => i.tableNo === tNo)
  }, [statusFiltered, tableFilter])

  const itemsInKitchen = useMemo(
    () =>
      tableFilteredItems.filter(
        (i) => i.kitchInfo?.status === KITCHEN_ITEM_STATUS.IN_KITCHEN
      ),
    [tableFilteredItems]
  )
  const itemsCooking = useMemo(
    () =>
      tableFilteredItems.filter(
        (i) => i.kitchInfo?.status === KITCHEN_ITEM_STATUS.COOKING
      ),
    [tableFilteredItems]
  )
  const itemsReadyOnly = useMemo(
    () =>
      tableFilteredItems.filter(
        (i) => i.kitchInfo?.status === KITCHEN_ITEM_STATUS.READY
      ),
    [tableFilteredItems]
  )

  const groupByTableSorted = (arr: (BillItem & { tableNo: number })[]) => {
    const map = new Map<number, (BillItem & { tableNo: number })[]>()
    for (const it of arr) {
      const list = map.get(it.tableNo) || []
      list.push(it)
      map.set(it.tableNo, list)
    }
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => getItemTime(a) - getItemTime(b))
      map.set(k, list)
    }
    return map
  }

  const inKitchenByTable = useMemo(
    () => groupByTableSorted(itemsInKitchen),
    [itemsInKitchen]
  )
  const cookingByTable = useMemo(
    () => groupByTableSorted(itemsCooking),
    [itemsCooking]
  )
  const readyByTable = useMemo(
    () => groupByTableSorted(itemsReadyOnly),
    [itemsReadyOnly]
  )

  const setItemChecked = (tableNo: number, sno: number, value: boolean) => {
    setChecked((prev) => ({
      ...prev,
      [tableNo]: { ...(prev[tableNo] || {}), [sno]: value },
    }))
  }
  const selectedCountForTable = (tableNo: number) =>
    Object.values(checked[tableNo] || {}).filter(Boolean).length

  const updateStatus = async (
    tableNo: number,
    sno: number,
    newStatus: KITCHEN_ITEM_STATUS
  ) => {
    try {
      const table = tables.find((t) => t.tableNo === tableNo)
      if (!table?.billSession?.items) return
      const clone: ITables = JSON.parse(JSON.stringify(table))
      if (!clone.billSession) return
      const idx = clone.billSession.items.findIndex((i) => i.sno === sno)
      if (idx > -1) {
        clone.billSession.items[idx].kitchInfo.status = newStatus
        await dispatch(updateCartItemOfTheTable({ table: clone }))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const printTablePipeline = async (tableNo: number) => {
    try {
      if (!printerUsb) return
      const table = tables.find((t) => t.tableNo === tableNo)
      const items = table?.billSession?.items || []
      const pipelineItems = items.filter((i) =>
        [KITCHEN_ITEM_STATUS.IN_KITCHEN, KITCHEN_ITEM_STATUS.COOKING].includes(
          i.kitchInfo?.status as KITCHEN_ITEM_STATUS
        )
      )
      if (pipelineItems.length === 0) return

      const data = await render(
        <RPrinter type="epson" characterSet="pc437_usa">
          <Text font="A" size={{ width: 2, height: 2 }} align="center" bold>
            KITCHEN TOKEN
          </Text>
          <Line />
          <Text font="A" size={{ width: 2, height: 2 }} align="center" bold>
            TABLE NO #{tableNo}
          </Text>
          <Line />
          <Br />
          {pipelineItems.map((i) => (
            <>
              <Text font="A" size={{ width: 2, height: 2 }} bold>
                {i.itemName}
              </Text>
              <Text font="A" size={{ width: 2, height: 2 }} bold>
                x{i.noOfItems} ({i.quantity}
                {i.quantityType})
              </Text>
            </>
          ))}
          <Cut />
        </RPrinter>
      )
      await printerUsb.print(data)
    } catch (e) {
      console.error(e)
    }
  }

  const printSelectedForTable = async (tableNo: number) => {
    try {
      if (!printerUsb) return
      const readyItemsForTable = (readyByTable.get(tableNo) || []).filter(
        (i) => checked[tableNo]?.[i.sno] ?? false
      )
      if (readyItemsForTable.length === 0) return

      const data = await render(
        <RPrinter type="epson" characterSet="pc437_usa">
          <Text font="A" size={{ width: 2, height: 2 }} align="center" bold>
            KITCHEN TOKEN
          </Text>
          <Line />
          <Text font="A" size={{ width: 2, height: 2 }} align="center" bold>
            TABLE NO #{tableNo}
          </Text>
          <Line />
          <Br />
          {readyItemsForTable.map((i) => (
            <>
              <Text font="A" size={{ width: 2, height: 2 }} bold>
                {i.itemName}
              </Text>
              <Text font="A" size={{ width: 2, height: 2 }} bold>
                x{i.noOfItems} ({i.quantity}
                {i.quantityType})
              </Text>
            </>
          ))}
          <Cut />
        </RPrinter>
      )
      await printerUsb.print(data)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          overflowX: 'hidden',
          alignItems: 'stretch',
          height: BOARD_HEIGHT,
          width: '100%',
        }}
      >
        {/* IN KITCHEN */}
        <KanbanColumn
          title="In Kitchen"
          count={itemsInKitchen.length}
          height={BOARD_HEIGHT}
        >
          {Array.from(inKitchenByTable.entries()).map(([tableNo, list]) => (
            <Box
              key={`ink-${tableNo}`}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: colorForTable(tableNo),
                border: (t) => `1px solid ${t.palette.divider}`,
                marginTop: '8px !important',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2" fontWeight={800}>
                  Table #{tableNo} • {list.length} item(s)
                </Typography>
                <CommonBtn
                  size="small"
                  variant="contained"
                  onClick={() => printTablePipeline(tableNo)}
                >
                  Print
                </CommonBtn>
              </Stack>
              <Stack spacing={1}>
                {list.map((it) => (
                  <ItemCard
                    key={`${tableNo}-${it.sno}`}
                    item={it}
                    onAdvance={() =>
                      updateStatus(tableNo, it.sno, KITCHEN_ITEM_STATUS.COOKING)
                    }
                    advanceLabel="Start Cooking"
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </KanbanColumn>

        {/* COOKING */}
        <KanbanColumn
          title="Cooking"
          count={itemsCooking.length}
          height={BOARD_HEIGHT}
        >
          {Array.from(cookingByTable.entries()).map(([tableNo, list]) => (
            <Box
              key={`cook-${tableNo}`}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: colorForTable(tableNo),
                border: (t) => `1px solid ${t.palette.divider}`,
                marginTop: '8px !important',
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} mb={1}>
                Table #{tableNo} • {list.length} item(s)
              </Typography>
              <Stack spacing={1}>
                {list.map((it) => (
                  <ItemCard
                    key={`${tableNo}-${it.sno}`}
                    item={it}
                    onAdvance={() =>
                      updateStatus(tableNo, it.sno, KITCHEN_ITEM_STATUS.READY)
                    }
                    advanceLabel="Ready"
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </KanbanColumn>

        {/* READY (no action) */}
        <KanbanColumn
          title="Ready/Print"
          count={itemsReadyOnly.length}
          height={BOARD_HEIGHT}
        >
          {Array.from(readyByTable.entries()).map(([tableNo, list]) => {
            const selectedCount = selectedCountForTable(tableNo)
            return (
              <Box
                key={`ready-${tableNo}`}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: colorForTable(tableNo),
                  border: (t) => `1px solid ${t.palette.divider}`,
                  marginTop: '8px !important',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    Table #{tableNo} • {list.length} item(s)
                  </Typography>
                  {selectedCount > 0 && (
                    <CommonBtn
                      size="small"
                      variant="contained"
                      onClick={() => printSelectedForTable(tableNo)}
                    >
                      Print Selected
                    </CommonBtn>
                  )}
                </Stack>
                <Stack spacing={1}>
                  {list.map((it) => (
                    <ItemCard
                      key={`${tableNo}-${it.sno}`}
                      item={it}
                      selectable
                      checked={!!checked[tableNo]?.[it.sno]}
                      onCheckChange={(v) => setItemChecked(tableNo, it.sno, v)}
                    />
                  ))}
                </Stack>
              </Box>
            )
          })}
        </KanbanColumn>
      </Stack>
    </Stack>
  )
}
